import axios from 'axios'

// const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.vestirstudio.com'
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001'

// Create axios instance for platform admin
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Request interceptor: send sessionStorage Bearer token + CSRF cookie header
api.interceptors.request.use(
  (config) => {
    // Bearer token from sessionStorage (set on login, cleared on tab close)
    const token = sessionStorage.getItem('platform_token')
    if (token) config.headers['Authorization'] = `Bearer ${token}`

    // CSRF double-submit for cookie-based auth (production)
    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrf_access_token='))
      ?.split('=')[1]
    if (csrfToken) config.headers['X-CSRF-TOKEN'] = csrfToken

    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config.url?.endsWith('/auth/logout') &&
      !window.location.pathname.endsWith('/login')
    ) {
      api.post('/api/platform/auth/logout').catch(() => {})
      sessionStorage.removeItem('platform_token')
      localStorage.removeItem('admin_user')
      window.location.href = '/login'
    }
    // Account still on its temporary password: the API refuses everything else
    if (
      error.response?.status === 403 &&
      error.response?.data?.error?.code === 'PASSWORD_CHANGE_REQUIRED' &&
      !window.location.pathname.startsWith('/account/password')
    ) {
      window.location.href = '/account/password'
    }
    return Promise.reject(error)
  }
)

// Platform Admin Auth API
export const authAPI = {
  login: (email, password) => api.post('/api/platform/auth/super-admin/login', { email, password }),
  getProfile: () => api.get('/api/platform/auth/profile'),
  logout: () => api.post('/api/platform/auth/logout'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/api/platform/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
}

// Platform admin users (vs-admin-ui operators) — owner only
export const adminUsersAPI = {
  getAll: () => api.get('/api/platform/admin-users'),
  create: (data) => api.post('/api/platform/admin-users', data),
  update: (userId, data) => api.put(`/api/platform/admin-users/${userId}`, data),
  resetPassword: (userId) => api.post(`/api/platform/admin-users/${userId}/reset-password`),
  remove: (userId) => api.delete(`/api/platform/admin-users/${userId}`),
}

// Platform Management API
export const platformAPI = {
  // Analytics
  getAnalytics: () => api.get('/api/platform/analytics/platform'),
  getRevenue: () => api.get('/api/platform/analytics/revenue'),
  
  // Tenant Management
  getTenants: (params = {}) => api.get('/api/platform/tenants', { params }),
  getTenantUsage: (tenantId) => api.get(`/api/platform/tenants/${tenantId}/usage`),
  getTenant: async (tenantId) => {
    try {
      // Try the individual tenant endpoint first
      return await api.get(`/api/platform/tenants/${tenantId}`)
    } catch (err) {
      // If we get a 405 Method Not Allowed, fallback to list endpoint
      if (err.response?.status === 405) {
        try {
          const listResponse = await api.get('/api/platform/tenants', { 
            params: { limit: 100 } 
          })
          
          const tenants = listResponse.data.data || listResponse.data.tenants || listResponse.data
          const tenant = tenants.find(t => t.id === tenantId)
          
          if (tenant) {
            return { data: tenant }
          } else {
            const notFoundError = new Error('Estúdio não encontrado')
            notFoundError.response = { status: 404, data: { error: 'Estúdio não encontrado na lista' } }
            throw notFoundError
          }
        } catch (fallbackErr) {
          throw fallbackErr
        }
      } else {
        // Re-throw other errors (401, 404, 500, etc.)
        throw err
      }
    }
  },
  createTenant: async (tenantData) => {
    try {
      const response = await api.post('/api/platform/tenants', tenantData)
      return response
    } catch (err) {
      throw err
    }
  },
  updateTenant: async (tenantId, tenantData) => {
    try {
      const response = await api.put(`/api/platform/tenants/${tenantId}`, tenantData)
      return response
    } catch (error) {
      throw error
    }
  },
  deleteTenant: (tenantId) => api.delete(`/api/platform/tenants/${tenantId}`),
  hardPurgeTenantBySlug: (slug) => api.delete(`/api/platform/tenants/by-slug/${slug}/purge`),
  
  // Tenant Status Management  
  activateTenant: (tenantId) => api.put(`/api/platform/tenants/${tenantId}`, { 
    is_active: true,
    subscription_status: 'active' 
  }),
  suspendTenant: (tenantId) => api.put(`/api/platform/tenants/${tenantId}`, { 
    is_active: false,
    subscription_status: 'suspended' 
  }),

  // Demo Data (super-admin only)
  seedDemoData: (slug, { language = 'pt', reseed = false } = {}) =>
    api.post(`/api/platform/tenants/by-slug/${slug}/demo-data`, { language, reseed }),
  purgeDemoData: (slug) =>
    api.delete(`/api/platform/tenants/by-slug/${slug}/demo-data`),
  getDemoDataStatus: (slug) =>
    api.get(`/api/platform/tenants/by-slug/${slug}/demo-data`),

  // Billing & Subscriptions
  getSubscriptions: (params = {}) => api.get('/api/platform/subscriptions', { params }),
  getTenantSubscription: (tenantId) => api.get(`/api/platform/tenants/${tenantId}/subscription`),
  changeTenantSubscription: (tenantId, data) => api.put(`/api/platform/tenants/${tenantId}/subscription`, data),
  getTenantSubscriptionHistory: (tenantId, params = {}) => api.get(`/api/platform/tenants/${tenantId}/subscription-history`, { params }),
  
  // Platform Settings
  getPlatformSettings: () => api.get('/api/platform/settings'),
  updatePlatformSettings: (settings) => api.put('/api/platform/settings', settings),
  
  // System Health
  getSystemHealth: () => api.get('/api/platform/health/platform'),
  // Unified support search (tenants, tenant users, clients for owners, support chats).
  // Optional: the command palette falls back to client-side search when it's missing.
  search: (q, config = {}) => api.get('/api/platform/search', { params: { q }, ...config }),
  getSystemMetrics: () => api.get('/api/platform/metrics')
}

// Subscription Tiers Management
export const subscriptionTiersAPI = {
  getAll: (params = {}) => api.get('/api/platform/subscription-tiers', { params }),
  getOne: (tierId) => api.get(`/api/platform/subscription-tiers/${tierId}`),
  create: (data) => api.post('/api/platform/subscription-tiers', data),
  update: (tierId, data) => api.put(`/api/platform/subscription-tiers/${tierId}`, data),
  remove: (tierId) => api.delete(`/api/platform/subscription-tiers/${tierId}`),
}

// Owner password reset link generation (super admin)
platformAPI.sendOwnerResetLink = (tenantId, options = {}) => {
  const { expiresInMinutes } = options
  return api.post(`/api/platform/tenants/${tenantId}/owner/reset-password-link`, {
    ...(expiresInMinutes ? { expires_in_minutes: expiresInMinutes } : {})
  })
}

// File upload helper (returns a public URL for the uploaded asset)
export const uploadAPI = {
  /**
   * Upload a file to the platform and return its public URL
   * Tries to use `/platform/uploads` by default, or `VITE_UPLOAD_URL` if provided
   */
  uploadFile: async (file, options = {}) => {
    const formData = new FormData()
    formData.append('file', file)

    const endpoint = import.meta.env.VITE_UPLOAD_URL || '/api/platform/uploads'

    try {
      const response = await api.post(endpoint, formData, {
        headers: {
          // Omit Content-Type so the browser sets multipart/form-data with boundary
          'Content-Type': false,
        },
        ...options,
      })

      // Try common response shapes
      const data = response?.data || {}
      const url = data?.url || data?.data?.url || data?.data?.Location || data?.Location
      if (!url) {
        throw new Error('Upload response did not include a URL')
      }
      return url
    } catch (error) {
      throw error
    }
  }
}

// Policies API (privacy policy & terms/conditions management)
export const notificationsAPI = {
  list: (params = {}) => api.get('/api/platform/notifications', { params }),
  getOne: (id) => api.get(`/api/platform/notifications/${id}`),
  create: (data) => api.post('/api/platform/notifications', data),
  update: (id, data) => api.put(`/api/platform/notifications/${id}`, data),
  remove: (id) => api.delete(`/api/platform/notifications/${id}`),
  stats: (id) => api.get(`/api/platform/notifications/${id}/stats`),
}

export const policiesAPI = {
  getAll: (params = {}) => api.get('/api/platform/policies', { params }),
  getOne: (policyId) => api.get(`/api/platform/policies/${policyId}`),
  getActive: (policyType) => api.get(`/api/platform/policies/active/${policyType}`),
  upload: (file, policyType, title = '') => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('policy_type', policyType)
    if (title) formData.append('title', title)
    return api.post('/api/platform/policies/upload', formData, {
      headers: { 'Content-Type': false },
    })
  },
  activate: (policyId) => api.put(`/api/platform/policies/${policyId}/activate`),
  remove: (policyId) => api.delete(`/api/platform/policies/${policyId}`),
}

// Tenant Data API (for inspecting tenant data)
export const tenantDataAPI = {
  getTenantClients: (tenantId, params = {}) => api.get(`/api/platform/tenants/${tenantId}/clients`, { params }),
  importTenantClientsCSV: (tenantId, file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/api/platform/tenants/${tenantId}/clients/import-csv`, formData, {
      headers: { 'Content-Type': false },
    })
  },
  getTenantProducts: (tenantId) => api.get(`/api/platform/tenants/${tenantId}/products`),
  getTenantUsers: (tenantId, params = {}) => api.get(`/api/platform/tenants/${tenantId}/users`, { params }),
  getTenantAnalytics: (tenantId) => api.get(`/api/platform/tenants/${tenantId}/analytics`),
  getTenantSupportChats: (tenantId, params = {}) => api.get(`/api/platform/tenants/${tenantId}/support-chats`, { params }),
  getTenantState: (tenantId) => api.get(`/api/platform/tenants/${tenantId}/state`),
  getWhatsappSession: (tenantId) => api.get(`/api/platform/tenants/${tenantId}/whatsapp/session`),
  stopWhatsappSession: (tenantId) => api.post(`/api/platform/tenants/${tenantId}/whatsapp/session/stop`),
  restartWhatsappSession: (tenantId) => api.post(`/api/platform/tenants/${tenantId}/whatsapp/session/restart`),
  logoutWhatsappSession: (tenantId) => api.post(`/api/platform/tenants/${tenantId}/whatsapp/session/logout`),
  deleteWhatsappSession: (tenantId) => api.delete(`/api/platform/tenants/${tenantId}/whatsapp/session`),
  // AI analysis module
  getAiSettings: (tenantId) => api.get(`/api/platform/tenants/${tenantId}/ai-settings`),
  updateAiSettings: (tenantId, data) => api.put(`/api/platform/tenants/${tenantId}/ai-settings`, data),
  updateTenantLimits: (tenantId, data) => api.put(`/api/platform/tenants/${tenantId}/limits`, data),
  getApiKey: (tenantId) => api.get(`/api/platform/tenants/${tenantId}/api-key`),
  generateApiKey: (tenantId) => api.post(`/api/platform/tenants/${tenantId}/api-key`),
  deleteApiKey: (tenantId) => api.delete(`/api/platform/tenants/${tenantId}/api-key`),
  getAiUsage: (tenantId, months = 6) => api.get(`/api/platform/tenants/${tenantId}/ai-usage`, { params: { months } }),
  // Support console. These endpoints are part of the support-console backend work and
  // may not be deployed yet — callers treat 404/405 as "not available" (isMissingEndpoint).
  getTenantNotes: (tenantId) => api.get(`/api/platform/tenants/${tenantId}/notes`),
  addTenantNote: (tenantId, text) => api.post(`/api/platform/tenants/${tenantId}/notes`, { text }),
  getTenantActivity: (tenantId, params = {}) => api.get(`/api/platform/tenants/${tenantId}/activity`, { params }),
  getFailedMessages: (tenantId, params = { since: '24h' }) =>
    api.get(`/api/platform/tenants/${tenantId}/whatsapp/failed-messages`, { params }),
  // Collaboration programs (Titanium & friends)
  getProgramAffiliation: (tenantId) => api.get(`/api/platform/tenants/${tenantId}/program-affiliation`),
  createProgramAffiliation: (tenantId, data) => api.post(`/api/platform/tenants/${tenantId}/program-affiliation`, data),
  updateProgramAffiliation: (tenantId, affiliationId, data) => api.put(`/api/platform/tenants/${tenantId}/program-affiliation/${affiliationId}`, data),
  revokeProgramAffiliation: (tenantId, affiliationId, reason) => api.delete(`/api/platform/tenants/${tenantId}/program-affiliation/${affiliationId}`, { data: reason ? { reason } : {} }),
}

// Collaboration Programs API (partner program catalog)
export const crmSyncAPI = {
  get: () => api.get('/api/platform/crm-sync'),
  update: (data) => api.put('/api/platform/crm-sync', data),
  run: () => api.post('/api/platform/crm-sync/run'),
}

export const collaborationProgramsAPI = {
  getAll: (params = {}) => api.get('/api/platform/collaboration-programs', { params }),
  create: (data) => api.post('/api/platform/collaboration-programs', data),
  update: (programId, data) => api.put(`/api/platform/collaboration-programs/${programId}`, data),
  remove: (programId) => api.delete(`/api/platform/collaboration-programs/${programId}`),
  runExpiry: (dryRun = false) => api.post('/api/platform/collaboration-programs/run-expiry', { dry_run: dryRun }),
}

// Proxy Management API (Webshare pool)
export const proxyAPI = {
  getAll: () => api.get('/api/platform/proxies'),
  sync: () => api.post('/api/platform/proxies/sync'),
  validate: () => api.post('/api/platform/proxies/validate'),
  assign: (proxyId, tenantId) => api.post(`/api/platform/proxies/${proxyId}/assign`, { tenant_id: tenantId }),
  unassign: (proxyId) => api.post(`/api/platform/proxies/${proxyId}/unassign`),
  updateTenantProxy: (tenantId, proxyId) => api.put(`/api/platform/tenants/${tenantId}/whatsapp/session/proxy`, proxyId ? { proxy_id: proxyId } : {}),
}

// Activity Analytics API (feature usage tracking)
export const activityAnalyticsAPI = {
  getTopFeatures: (params = {}) => api.get('/api/platform/analytics/events/top-features', { params }),
  getComparison: (params = {}) => api.get('/api/platform/analytics/events/comparison', { params }),
  getTimeline: (params = {}) => api.get('/api/platform/analytics/events/timeline', { params }),
  getByTenant: (params = {}) => api.get('/api/platform/analytics/events/by-tenant', { params }),
  getPeakHours: (params = {}) => api.get('/api/platform/analytics/events/peak-hours', { params }),
  runAggregation: () => api.post('/api/platform/analytics/events/aggregate'),
}

// Support Chats API (all chats across platform)
export const supportChatsAPI = {
  getAll: (params = {}) => api.get('/api/platform/support-chats', { params }),
  getOne: (chatId) => api.get(`/api/platform/support-chats/${chatId}`),
  generateOverview: (data) => api.post('/api/platform/support-chats/ai-overview', data),
  getReports: (params = {}) => api.get('/api/platform/support-chats/ai-overview/reports', { params }),
}

// Public questionnaire funnel activity — one row per visitor session, including the
// sessions that were spam-discarded or errored and so never became a client.
export const publicFormActivityAPI = {
  list: (params = {}) => api.get('/api/platform/public-form-activity', { params }),
  getOne: (activityId) => api.get(`/api/platform/public-form-activity/${activityId}`),
  stats: (params = {}) => api.get('/api/platform/public-form-activity/stats', { params }),
  purge: (data = {}) => api.post('/api/platform/public-form-activity/purge', data),
}

// WhatsApp Support Triage Report (scheduled WAHA group report)
export const supportReportAPI = {
  getGroups: (tenantId, sync = false) =>
    api.get('/api/platform/whatsapp/groups', { params: { tenant_id: tenantId, sync } }),
  runNow: () => api.post('/api/platform/support-report/run'),
  getReports: () => api.get('/api/platform/support-report/reports'),
}

export default api 